<script setup lang="ts">
import { backgroundStyling } from 'lib/storyblok'
import { processImage } from 'lib/images/storyblok'
import { parseURL } from 'lib/url'
import type { ContainerSchema } from 'types/storyblok/container'

// components: getNestableComponents('container'),

interface Props {
  story: ContainerSchema
  position?: number
}

const props = withDefaults(defineProps<Props>(), {
  position: -1,
})

const mediaStore = useMediaStore()

const hasBorder = computed(
  () =>
    props.story.image_border_top
    || props.story.image_border_bottom
    || props.story.image_border_left
    || props.story.image_border_right,
)

const borders = ['top', 'bottom', 'left', 'right']
  .map((border, index) => ({
    _key: index + 1,
    className: border,
    property: `image_border_${border}` as keyof ContainerSchema,
  }))
  .filter(
    ({ property }) => Object.hasOwnProperty.call(props.story, property) && props.story[property],
  )
  .map((border) => {
    const backgroundImage = `url('${processImage(parseURL(props.story[border.property]), {
      fallback: true,
    })}')`

    return {
      ...border,
      backgroundImage,
    }
  })
  .filter(Boolean)

const background = computed(() => backgroundStyling(props.story))

const textColor = computed(() => {
  if (mediaStore.isMobile && props.story.mobile_text_color)
    return { color: props.story.mobile_text_color }

  if (mediaStore.isDesktop && props.story.desktop_text_color)
    return { color: props.story.desktop_text_color }

  return ''
})

const bgPosition = computed(() => {
  return props.story.custom_background_position
    ? { backgroundPosition: props.story.custom_background_position }
    : ''
})

const bgRepeatSize = computed(() => {
  if (props.story.background_repeat === 'bg-repeat') {
    return props.story.background_repeat_size
      ? { backgroundSize: props.story.background_repeat_size }
      : ''
  }

  return ''
})

const customPadding = computed(() => {
  if (mediaStore.isMobile && props.story.mobile_padding_custom)
    return { padding: props.story.mobile_padding_custom }

  if (mediaStore.isDesktop && props.story.desktop_padding_custom)
    return { padding: props.story.desktop_padding_custom }

  return ''
})
</script>

<template>
  <section
    v-editable="story"
    class="section"
    :class="[
      `mp--${story.mobile_padding}`,
      `dp--${story.desktop_padding}`,
      `mtc--${story.mobile_text_color}`,
      `dtc--${story.desktop_text_color}`,
      `mbgc--${story.mobile_background_color}`,
      `dbgc--${story.desktop_background_color}`,
      story.desktop_indent,
      story.background_position,
      story.background_size,
      story.background_repeat,
      story.background_tint,
      story.style_modifier,
      story.mobile_display,
      story.desktop_display,
    ]"
    :style="[background, textColor, bgRepeatSize, bgPosition, customPadding]"
  >
    <div
      :class="[
        { container: story.set_container_for_big_screens },
        `mc--${story.mobile_columns}`,
        `dc--${story.desktop_columns}`,
        `mg--${story.mobile_gutter}`,
        `dg--${story.desktop_gutter}`,
        `ma--${story.mobile_align_items}`,
        `da--${story.desktop_align_items}`,
        `mta--${story.mobile_textalign}`,
        `dta--${story.desktop_textalign}`,
      ]"
    >
      <div v-if="hasBorder" class="image-border">
        <div
          v-for="border in borders"
          :key="border._key"
          :class="border"
          :style="{
            backgroundImage: border.backgroundImage,
          }"
        />
      </div>
      <component
        :is="`${item.component}`"
        v-for="item in story.content"
        :key="item._uid"
        :name="`${item.component}`"
        :story="item"
        :position="position"
      />
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import 'assets/scss/legacy/_variables.scss';

.section {
  width: 100%;
  position: relative;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
  &.bg-pos-fixed {
    background-attachment: fixed;
  }
  &.bg-repeat {
    background-size: unset;
    background-repeat: repeat;
  }
  &.bg-size-contain {
    background-size: contain;
  }
  &.tint::before {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: black;
    opacity: 0.3;
    z-index: 0;
  }

  /*
   * If something is floated, clear it
   */
  &:after {
    content: '';
    height: 0;
    clear: both;
    visibility: hidden;
  }

  /*
   * Style Modifiers
   * Options are extended in Storyblok and outputs a class
   * that can be used here for special purpose styling
   */
  &.beatles-border {
    border: 0.3rem solid #ffd424;
    &::before {
      position: absolute;
      pointer-events: none !important;
      content: '';
      display: block;
      width: calc(100% - 1.2rem);
      height: calc(100% - 1.2rem);
      border: 0.3rem solid #ffd424;
      top: 0.6rem;
      left: 0.6rem;
      box-sizing: border-box;
    }
  }

  /*
   * Image Borders
   * Are added as separate top/bottom/left/right images,
   * repeated respectively as a border around the container.
   * Was added with the Queen campaign as use-case.
   */
  .image-border {
    pointer-events: none;
    width: 100%;
    height: 100%;
    .top,
    .bottom,
    .left,
    .right {
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
    }
    .top {
      background-repeat: repeat-x;
      background-position: top center;
      background-size: auto 2rem;
      z-index: 2;
    }
    .bottom {
      background-repeat: repeat-x;
      background-position: bottom center;
      background-size: auto 2rem;
      z-index: 2;
    }
    .left {
      background-repeat: repeat-y;
      background-position: center left;
      background-size: 2rem auto;
    }
    .right {
      background-repeat: repeat-y;
      background-position: center right;
      background-size: 2rem auto;
    }
  }
}

// Tablet
@media screen and (min-width: $tablet) {
  .section {
    .image-border {
      .top {
        background-size: auto 3.5rem;
      }
      .bottom {
        background-size: auto 3.5rem;
      }
      .left {
        background-size: 3.5rem auto;
      }
      .right {
        background-size: 3.5rem auto;
      }
    }
  }
}
// Laptop
@media screen and (min-width: $laptop) {
  .section {
    .image-border {
      .top {
        background-size: auto 5rem;
      }
      .bottom {
        background-size: auto 5rem;
      }
      .left {
        background-size: 5rem auto;
      }
      .right {
        background-size: 5rem auto;
      }
    }
  }
}
</style>
